import React, { useState } from "react";
import { useLocation, useParams } from "react-router-dom";

import { Pagination } from "@performyard/py-components";
import type { MeetingModelJson } from "@performyard/service-contracts";
import { ADMIN_ROLES } from "@performyard/utils-isomorphic";

import FilterChips from "../../../components/FilterChips";
import {
	Breadcrumbs,
	CreateMeetingModal,
	DataTable,
	EditMeetingModal,
	FilterPanel,
	PageHeader,
	Toolbar,
} from "./components";

import { useCurrentUser, useMeetingWorkspaceDetails, useMeetingsSearch } from "../../../api/hooks";
import { useFeatureFlagQuery } from "../../../hooks/useFeatureFlag";
import useForm from "../../../hooks/useForm";
import { useWorkspaceNavigationState } from "./hooks";

import { queryClient } from "../../../api/queryClient";

import { toAvatarGroupEmployees } from "../utils/participants";
import { convertFiltersToFilterChips, convertFiltersToSearchParams, getHasFiltersApplied } from "./utils";

import BasePageTemplate from "../../../pageTemplates/BasePageTemplate";
import useInfoPageTemplateQueryState from "../../../pageTemplates/InfoPageTemplate/useInfoPageTemplateQueryState";
import ErrorPage from "../../Error";
import { config } from "./config";
import { type MeetingsTableFilters, MeetingsTableFiltersSchema } from "./schema";

const MeetingsTable = (): React.ReactElement | null => {
	const { workspaceId } = useParams();
	const location = useLocation();
	const previousSearchParams = new URLSearchParams(location.search).get("from") || "";

	const { data: currentUser } = useCurrentUser();
	const { data: workspace, isLoading: isLoadingWorkspace } = useMeetingWorkspaceDetails(workspaceId || "");
	const { value: ffMeetings, query: ffMeetingsQuery } = useFeatureFlagQuery("ff-is-meetings-enabled", false);

	const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
	const [isCreateMeetingModalOpen, setIsCreateMeetingModalOpen] = useState(false);
	const [editingMeeting, setEditingMeeting] = useState<MeetingModelJson | null>(null);

	const {
		filters,
		setFilters,
		sort,
		setSort,
		searchValue,
		debouncedSearchValue,
		setDebouncedSearch,
		pageNumber,
		setPageNumber,
	} = useInfoPageTemplateQueryState<MeetingsTableFilters>({
		defaultSortName: config.defaultSortName,
		defaultSortDirection: config.defaultSortDirection,
		defaultPageNumber: config.defaultPageNumber,
		fallbackFilters: config.fallbackFilters,
	});

	const userId = currentUser?.me?.authorization?.payload.user_id || "";
	const userRole = currentUser?.me?.authorization?.payload.user_primary_role || "";
	const isAdmin = ADMIN_ROLES.includes(userRole);
	const isParticipant = workspace?.participants?.some((p) => p.userId === userId) ?? false;

	useWorkspaceNavigationState({ isAdmin, isParticipant });

	const {
		data: meetings,
		totalCount: totalMeetingsCount,
		isFetching: isFetchingMeetings,
	} = useMeetingsSearch<"workspaceDetails">(
		convertFiltersToSearchParams({
			filters,
			sort,
			pageNumber,
			workspaceId: workspaceId || "",
			userId,
			searchTerm: debouncedSearchValue,
		})
	);

	const {
		state: filterFormState,
		updateForm: updateFilterForm,
		setInitialState: setInitialFilterForm,
	} = useForm({
		schema: MeetingsTableFiltersSchema,
		defaultValues: filters,
		disableWarning: true,
	});

	const workspaceParticipants = toAvatarGroupEmployees(workspace?.participants, { currentUserId: userId });

	// Wait for feature flag to load, then show access denied if disabled
	if (ffMeetingsQuery.isLoading) {
		return null;
	}

	if (!ffMeetings) {
		return <ErrorPage code={403} />;
	}

	// Wait for workspace fetch before deciding 404 / access. If the workspace
	// legitimately doesn't exist (bad URL, deleted) show a 404 instead of the
	// empty page shell.
	if (isLoadingWorkspace) {
		return null;
	}

	if (!workspace) {
		return <ErrorPage code={404} />;
	}

	// Non-admin, non-participant users shouldn't see the meetings list even if
	// they know the workspace id. MeetingDetails already enforces this; keep
	// the two pages consistent.
	if (!isAdmin && !isParticipant) {
		return <ErrorPage code={403} />;
	}

	const hasFiltersApplied = getHasFiltersApplied(filters);
	const hasSearchApplied = debouncedSearchValue.trim() !== "";
	const hasMeetingsOrFilters = meetings?.length > 0 || hasFiltersApplied || hasSearchApplied;

	const handleAddMeetingClick = (): void => {
		queryClient.invalidateQueries({
			queryKey: ["get-meeting-carry-over-items-by-workspace"],
		});
		setIsCreateMeetingModalOpen(true);
	};

	const handleDeleteFilterChip = (id: string): void => {
		const currentFilters = { ...filters };

		if (id === "status") {
			currentFilters.status = [];
		} else if (id === "privateNotes") {
			currentFilters.privateNotes = "any";
		} else if (id === "meetingDate") {
			currentFilters.meetingDateStart = undefined;
			currentFilters.meetingDateEnd = undefined;
		}

		setFilters(currentFilters);
		setInitialFilterForm(currentFilters, true);
	};

	return (
		<>
			<BasePageTemplate title={workspace?.name || "Meeting Workspace"}>
				<Breadcrumbs
					workspaceName={workspace?.name || ""}
					workspaceId={workspaceId || ""}
					previousSearchParams={previousSearchParams}
				/>
				<PageHeader workspaceName={workspace?.name || ""} participants={workspaceParticipants} />
				<Toolbar
					searchValue={searchValue}
					onSearchChange={setDebouncedSearch}
					onFilterClick={() => setIsFilterPanelOpen(true)}
					isFilterDisabled={!hasMeetingsOrFilters}
					onAddMeetingClick={handleAddMeetingClick}
					workspaceId={workspaceId || ""}
					filters={filters}
					meetings={meetings}
					userId={userId}
					isAdmin={isAdmin}
					participantCount={workspaceParticipants.length}
				/>
				<div className="h-4" />
				{hasFiltersApplied && (
					<>
						<FilterChips
							filters={convertFiltersToFilterChips(filters)}
							label="Filters applied: "
							onDeleteFilter={handleDeleteFilterChip}
						/>
						<div className="h-4" />
					</>
				)}
				<DataTable
					meetings={meetings}
					isFetchingMeetings={isFetchingMeetings}
					activeSort={sort.name}
					activeSortDirection={sort.direction}
					handleColumnHeaderClick={(column) => setSort(column)}
					userId={userId}
					onEditMeeting={setEditingMeeting}
					workspaceId={workspaceId || ""}
					workspaceCreatorId={workspace?.created?.userId || ""}
				/>
				{totalMeetingsCount > config.pageSize && (
					<>
						<div className="h-4" />
						<div className="flex justify-center">
							<Pagination
								currentPage={pageNumber}
								pageCount={Math.ceil(totalMeetingsCount / config.pageSize)}
								onPageChange={(page: number) => setPageNumber(page)}
							/>
						</div>
					</>
				)}
			</BasePageTemplate>
			<FilterPanel
				isOpen={isFilterPanelOpen}
				onClose={() => setIsFilterPanelOpen(false)}
				onApplyFilters={(newFilters) => {
					setFilters(newFilters);
					setInitialFilterForm(newFilters, true);
				}}
				filters={filterFormState}
				updateForm={updateFilterForm}
			/>
			{workspace && (
				<>
					<CreateMeetingModal
						isOpen={isCreateMeetingModalOpen}
						handleClose={() => setIsCreateMeetingModalOpen(false)}
						workspace={workspace}
						workspaceHasMeetings={meetings?.length > 0}
						setExpandedMeetingIds={() => {}}
					/>
					<EditMeetingModal
						isOpen={Boolean(editingMeeting)}
						handleClose={() => setEditingMeeting(null)}
						meeting={editingMeeting}
					/>
				</>
			)}
		</>
	);
};

export default MeetingsTable;

// Re-exports for external consumers
export { config } from "./config";
export type { MeetingsTableFilters } from "./schema";
export type { MeetingTableRow, MeetingTableColumn } from "./types";
export { useWorkspaceNavigationState, useCalendarIntegrationStatus, useMeetingCalendarIntegration } from "./hooks";
